import {useCallback, useMemo} from 'react'
import {
    useAccessToken,
    useShopperContext,
    useShopperContextsMutation,
    useUsid
} from '@salesforce/commerce-sdk-react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// relative paths necessary for test suite
import BasketMgr from '../custom-api/basket-custom-api'
import useMultiSite from './use-multi-site'
import useShipToCountry from './use-ship-to-country'
import useSiteType from './use-site-type'
import {useGTM} from './use-gtm'
import {getRedirectLanguage, getShipToCountry} from '../utils/ship-to-country-utils'
import {
    PARTNER_SHIP_TO_COUNTRIES,
    getShipToCountryChangeTarget,
    persistShipToCountryCookies
} from '../utils/ship-to-country-change-target'

/**
 * Returns the currently applied ship-to country code from shopper context or app state.
 *
 * @param {Object} params - Current context params.
 * @param {Object} params.shopperContext - Shopper context response.
 * @param {Object} params.shipToCountry - Current ship-to country state.
 * @returns {string|undefined} Current ship-to country code.
 */
const getCurrentShipToCountryCode = ({shopperContext, shipToCountry}) =>
    shopperContext?.geoLocation?.countryCode ||
    shopperContext?.geoLocation?.country ||
    shipToCountry?.id

/**
 * Shared hook for applying a ship-to country change.
 *
 * It centralizes Shopper Context creation/update, local ship-to state, SSR cookies,
 * SFS basket assignment, GTM tracking, and redirect target resolution.
 *
 * @returns {Object} Ship-to country application API.
 */
export const useApplyShipToCountry = () => {
    const {site, locale, buildUrl, isOutletSite} = useMultiSite()
    const {shipToCountry, setShipToCountry} = useShipToCountry()
    const {isUS} = useSiteType()
    const {triggerPopinChangeShipToEvent} = useGTM()
    const {app: appConfig} = getConfig()

    const {usid, getUsidWhenReady} = useUsid()
    const {getTokenWhenReady} = useAccessToken()

    const {data: shopperContext, isLoading: isShopperContextLoading} = useShopperContext(
        {
            parameters: {usid}
        },
        {enabled: Boolean(!isServer && usid)}
    )

    const createShopperContext = useShopperContextsMutation('createShopperContext')
    const updateShopperContext = useShopperContextsMutation('updateShopperContext')

    const basketMgr = useMemo(
        () =>
            new BasketMgr({
                parameters: {
                    siteId: site.id,
                    organizationId: appConfig.commerceAPI.parameters.organizationId,
                    locale: locale.id
                }
            }),
        [appConfig.commerceAPI.parameters.organizationId, locale.id, site.id]
    )

    const updateBasketAssignments = useCallback(
        async (countryCode, resetAssignments = false) => {
            const accessToken = await getTokenWhenReady()
            await basketMgr.sfsBasketAssignments(
                accessToken,
                site.id,
                locale.id,
                false,
                countryCode,
                resetAssignments
            )
        },
        [basketMgr, getTokenWhenReady, locale.id, site.id]
    )

    const setShopperContextCountry = useCallback(
        async ({id, currency}, options = {}) => {
            const {updateBasketAssignments: shouldUpdateBasketAssignments = true} = options
            const currentUsid = usid || (await getUsidWhenReady())

            const customQualifiers = {
                ...(shopperContext?.customQualifiers || {}),
                currency,
                locale: locale?.id
            }

            const shopperContextOptions = {
                parameters: {
                    usid: currentUsid,
                    siteId: site.id
                },
                body: {
                    geoLocation: {
                        countryCode: id
                    },
                    customQualifiers
                }
            }

            const hasExistingShopperContext = Boolean(
                shopperContext?.geoLocation || shopperContext?.customQualifiers
            )

            if (hasExistingShopperContext) {
                await updateShopperContext.mutateAsync(shopperContextOptions)
            } else {
                await createShopperContext.mutateAsync(shopperContextOptions)
            }

            if (shouldUpdateBasketAssignments) {
                await updateBasketAssignments(id, true)
            }
        },
        [
            createShopperContext,
            getUsidWhenReady,
            locale?.id,
            shopperContext,
            site.id,
            updateBasketAssignments,
            updateShopperContext,
            usid
        ]
    )

    const getCountryChangeTarget = useCallback(
        (selectedCountryCode) => {
            const isSwitchingSite =
                (selectedCountryCode === 'US' && ['eu', 'outlet-eu'].includes(site.alias)) ||
                (selectedCountryCode !== 'US' &&
                    !PARTNER_SHIP_TO_COUNTRIES.includes(selectedCountryCode) &&
                    ['us', 'outlet-us'].includes(site.alias))

            const preferredLocale = getRedirectLanguage(
                selectedCountryCode,
                isSwitchingSite ? null : locale?.id
            )

            return getShipToCountryChangeTarget({
                selectedCountryCode,
                siteAlias: site.alias,
                isOutletSite,
                buildUrl,
                preferredLocale
            })
        },
        [buildUrl, isOutletSite, locale?.id, site.alias]
    )

    const applyShipToCountry = useCallback(
        async (selectedCountryCode, options = {}) => {
            const {
                accepted = true,
                updateShopperContext = true,
                updateLocalState = true,
                updateCookies = true,
                updateBasketAssignments: shouldUpdateBasketAssignments = true,
                triggerGtm = true
            } = options

            if (!selectedCountryCode) {
                throw new Error('Missing selected ship-to country.')
            }

            const previousShipToCountry = getCurrentShipToCountryCode({
                shopperContext,
                shipToCountry
            })

            const target = getCountryChangeTarget(selectedCountryCode)

            if (triggerGtm) {
                triggerPopinChangeShipToEvent(previousShipToCountry, selectedCountryCode)
            }

            if (target.type === 'partner') {
                return target
            }

            if (target.type === 'siteSwitch') {
                if (updateCookies) {
                    persistShipToCountryCookies(selectedCountryCode, accepted)
                }

                return target
            }

            const selectedShipToCountry = getShipToCountry(selectedCountryCode, isUS)

            if (!selectedShipToCountry) {
                throw new Error(`Unsupported ship-to country: ${selectedCountryCode}`)
            }

            if (updateShopperContext) {
                await setShopperContextCountry(selectedShipToCountry, {
                    updateBasketAssignments: shouldUpdateBasketAssignments
                })
            } else if (shouldUpdateBasketAssignments) {
                await updateBasketAssignments(selectedShipToCountry.id, true)
            }

            if (updateLocalState) {
                setShipToCountry(selectedShipToCountry)
            }

            if (updateCookies) {
                persistShipToCountryCookies(selectedShipToCountry.id, accepted)
            }

            return {
                ...target,
                shipToCountry: selectedShipToCountry
            }
        },
        [
            getCountryChangeTarget,
            isUS,
            setShipToCountry,
            setShopperContextCountry,
            shipToCountry,
            shopperContext,
            triggerPopinChangeShipToEvent,
            updateBasketAssignments
        ]
    )

    return {
        applyShipToCountry,
        getCountryChangeTarget,
        persistShipToCountryCookies,
        isShopperContextLoading,
        shopperContext,
        shipToCountry
    }
}
